/* eslint-disable no-unused-vars */
import { type PhoneNumberInput } from '../common/common'

export enum StartWebAuthnAuthenticationErrorType {
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  PENDING_USER_UNABLE_TO_AUTHENTICATE = 'PENDING_USER_UNABLE_TO_AUTHENTICATE',
}

export enum AuthenticateWebAuthnErrorType {
  UNABLE_TO_AUTHENTICATE = 'UNABLE_TO_AUTHENTICATE',
  NO_ACTIVE_WEBAUTHN_REGISTRATION = 'NO_ACTIVE_WEBAUTHN_REGISTRATION',
  INVALID_PUBLIC_KEY_CREDENTIAL = 'INVALID_PUBLIC_KEY_CREDENTIAL',
  S_BLOCKED = 'S_BLOCKED',
  OTP_VERIFICATION_REQUIRED = 'OTP_VERIFICATION_REQUIRED',
  UNABLE_TO_SEND_LOGIN_VERIFICATION_OTP = 'UNABLE_TO_SEND_LOGIN_VERIFICATION_OTP',
  NO_PASSKEYS_FOUND = 'NO_PASSKEYS_FOUND',
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  CHALLENGE_NOT_FOUND = 'CHALLENGE_NOT_FOUND',
  DEVICE_RISK_BLOCKED = 'DEVICE_RISK_BLOCKED',
}

export interface StartWebAuthnAuthenticationInput {
  email?: string
}

export interface StartWebAuthnAuthenticationSuccess {
  __typename: string
  publicKeyCredentialRequestOptions: string
}

export interface IntelligenceResult {
  userMessage?: string
  suggestedActions?: string[]
  fallbackOptions?: string[]
}

export interface StartWebAuthnAuthenticationError {
  __typename: string
  errorType?: StartWebAuthnAuthenticationErrorType
  intelligenceResult?: IntelligenceResult
}

export interface AuthenticateWebAuthnInput {
  publicKeyCredentials: string
}

export interface AuthenticateWebAuthnSuccess {
  __typename: string
  customerId?: string
  userId?: string
  isManualReviewApplicant?: boolean
}

export interface AuthenticateWebAuthnError {
  __typename: string
  errorType?: AuthenticateWebAuthnErrorType
}

export interface PingSessionOutput {
  __typename: string
  userId?: string
}

export interface SignOutOutput {
  isSuccessful: boolean
}

export interface RegisterWebAuthnInput {
  publicKeyCredentials: string
}

export interface RegisterWebAuthnSuccess {
  __typename: 'RegisterWebAuthnSuccess'
  userId?: string
}

export enum RegisterWebAuthnErrorType {
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  DUPLICATE_WEBAUTHN_REGISTRATION = 'DUPLICATE_WEBAUTHN_REGISTRATION',
  PENDING_USER_UNABLE_TO_REGISTER = 'PENDING_USER_UNABLE_TO_REGISTER',
  INVALID_PUBLIC_KEY_CREDENTIAL = 'INVALID_PUBLIC_KEY_CREDENTIAL',
}

export interface RegisterWebAuthnError {
  __typename: 'RegisterWebAuthnError'
  errorType?: RegisterWebAuthnErrorType
}

export type RegisterWebAuthnOutput = RegisterWebAuthnSuccess | RegisterWebAuthnError

export interface StartWebAuthnRegistrationInput {
  isMobileWithPlatformAuthenticator?: boolean
}

export enum StartWebAuthnRegistrationErrorType {
  USER_NOT_FOUND = 'USER_NOT_FOUND',
}

export interface StartWebAuthnRegistrationSuccess {
  __typename: 'StartWebAuthnRegistrationSuccess'
  publicKeyCredentialCreationOptions: string
}

export interface StartWebAuthnRegistrationError {
  __typename: 'StartWebAuthnRegistrationError'
  errorType?: StartWebAuthnRegistrationErrorType
}

export type StartWebAuthnRegistrationOutput = StartWebAuthnRegistrationSuccess | StartWebAuthnRegistrationError

export enum AccountVerificationType {
  PHONE_OTP = 'PHONE_OTP',
  EMAIL_OTP = 'EMAIL_OTP',
  EMAIL_MAGIC_LINK = 'EMAIL_MAGIC_LINK',
}

export interface AccountRecoveryInput {
  verifyType?: AccountVerificationType
  sendVerificationMethod?: boolean
  email?: string
  phone?: PhoneNumberInput
  verificationToken?: string
  publicKeyCredentials?: string
  isMobileWithPlatformAuthenticator?: boolean
}

export interface AccountRecoveryInputV2 {
  verifyType?: AccountVerificationType
  sendVerificationMethod?: boolean
  email?: string
  phone?: PhoneNumberInput
  verificationToken?: string
  publicKeyCredentials?: string
  isMobileWithPlatformAuthenticator?: boolean
}

export enum AccountRecoveryStatus {
  NEW = 'NEW',
  VERIFICATION_SENT = 'VERIFICATION_SENT',
  VERIFICATION_CONFIRMED = 'VERIFICATION_CONFIRMED',
  PENDING_DOCV_RESULT = 'PENDING_DOCV_RESULT',
  REGISTER_WEBAUTHN = 'REGISTER_WEBAUTHN',
  REGISTER_WEBAUTHN_SUCCESS = 'REGISTER_WEBAUTHN_SUCCESS',
  IN_REVIEW = 'IN_REVIEW',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
}

export interface AccountRecoveryResponse {
  __typename: 'AccountRecoveryResponse'
  documentVerificationUrl?: string
  publicKeyCredentialCreationOptions?: string
  status: AccountRecoveryStatus
}

export interface AccountRecoveryResponseV2 {
  __typename: 'AccountRecoveryResponseV2'
  documentVerificationToken?: string
  publicKeyCredentialCreationOptions?: string
  status: AccountRecoveryStatus
  verificationId?: string
  documentVerificationBaseUrl?: string
}

export enum AccountRecoveryErrorType {
  UNABLE_TO_VALIDATE_VERIFICATION_METHOD = 'UNABLE_TO_VALIDATE_VERIFICATION_METHOD',
  INVALID_EMAIL = 'INVALID_EMAIL',
  INVALID_PHONE = 'INVALID_PHONE',
  WEBAUTHN_REGISTRATION_FAILED = 'WEBAUTHN_REGISTRATION_FAILED',
  NOT_FOUND = 'NOT_FOUND',
  EXPIRED = 'EXPIRED',
}

export interface AccountRecoveryError {
  __typename: 'AccountRecoveryError'
  errorType?: AccountRecoveryErrorType
}

export type AccountRecoveryOutput = AccountRecoveryResponse | AccountRecoveryError

export type AccountRecoveryOutputV2 = AccountRecoveryResponseV2 | AccountRecoveryError

export interface LoginOtpVerificationInput {
  otpCode?: string
  resendVerification?: boolean
}

export type LoginOtpVerificationOutput = LoginOtpVerification | ResendOtp | LoginOtpVerificationError

export interface LoginOtpVerification {
  __typename: 'LoginOtpVerification'
  userId: string
  isManualReviewApplicant?: boolean
}

export interface LoginOtpVerificationError {
  __typename: 'LoginOtpVerificationError'
  errorType: LoginOtpVerificationErrorType
}

export enum LoginOtpVerificationErrorType {
  USER_LOGIN_BLOCKED = 'USER_LOGIN_BLOCKED',
  S_BLOCKED = 'S_BLOCKED',
  OTP_VERIFICATION_FAILED = 'OTP_VERIFICATION_FAILED', // can resend OTP
  LOGIN_FAILED = 'LOGIN_FAILED', // error page
}

export interface ResendOtp {
  __typename: 'ResendOtp'
  isSuccessful: boolean
}

export enum SessionType {
  AUTHENTICATED = 'AUTHENTICATED',
  ANONYMOUS = 'ANONYMOUS',
}

export interface RefreshTokenResponse {
  __typename: 'RefreshTokenResponse'
  accessToken: string
  sessionType: SessionType
  expiresAt: string // DateTime as ISO string from GraphQL
  userId: string | null
}

// Additional passkey-related types from node-mono passkey domain
export interface RemovePasskeyInput {
  credentialId: string
}

export interface RemovePasskeySuccess {
  __typename: 'RemovePasskeySuccess'
  success: boolean
}

export enum RemovePasskeyErrorType {
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  PASSKEY_NOT_FOUND = 'PASSKEY_NOT_FOUND',
  UNAUTHORIZED = 'UNAUTHORIZED',
}

export interface RemovePasskeySuccess {
  __typename: 'RemovePasskeySuccess'
  success: boolean
}

export interface RemovePasskeyError {
  __typename: 'RemovePasskeyError'
  errorType?: RemovePasskeyErrorType
}

export type RemovePasskeyOutput = RemovePasskeySuccess | RemovePasskeyError

// Enhanced error types for WebAuthn authentication (matching node-mono)
export enum StartWebAuthnAuthenticationErrorTypeEnhanced {
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  NO_PASSKEYS_FOUND = 'NO_PASSKEYS_FOUND',
}

export enum AuthenticateWebAuthnErrorTypeEnhanced {
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  INVALID_PUBLIC_KEY_CREDENTIAL = 'INVALID_PUBLIC_KEY_CREDENTIAL',
  CHALLENGE_NOT_FOUND = 'CHALLENGE_NOT_FOUND',
  PASSKEY_NOT_FOUND = 'PASSKEY_NOT_FOUND',
}

export interface AuthSignOutInput {
  signOutAllDevices?: boolean
}

export interface AuthSignOutResponse {
  success: boolean
  message: string
}

export interface AuthsvsPasskeysOutput {
  id: string
  userId: string
  credentialId: string
  createdAt: string
  updatedAt: string
  aaguid?: string
  devicePlatform?: string
  deviceOs?: string
  deviceOsVersion?: string
  browserName?: string
  browserVersion?: string
  userAgent?: string
  isMobile?: boolean
  transports?: string[]
  nickName?: string
}
